import { useEffect, useMemo, useRef, useState } from "react";
import Table from "@/components/Table/Table";
import Button from "@/components/Button/Button";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import PencilIcon from "@/components/SVGComponents/PencilIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import CustomTabs from "@/components/CustomTabs/CustomTabs";
import type { Column } from "@/types/common";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getAdminUsers } from "@/store/slices/userSlice";
import { getRoles } from "@/store/slices/roleSlice";
import userService from "@/services/api/user";
import type { User } from "@/services/api/user";
import roleService from "@/services/api/roles";
import type { Role } from "@/services/api/roles";
import toast from "@/lib/toast";
import { formatDate } from "@/lib/dateFormat";
import { getDisplayName as getDisplayNameBase, getInitials } from "@/lib/user";
import InviteAdminModal from "./components/InviteAdminModal/InviteAdminModal";
import NewRoleModal from "./components/NewRoleModal/NewRoleModal";
import type { NewRoleFormValues } from "./components/NewRoleModal/NewRoleModal";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import Pagination from "@/components/Table/Pagination";
import AdminCard from "./components/AdminCard";
import RoleCard from "./components/RoleCard";
import styles from "./TeamRoles.module.scss";
import { TableCellTooltip } from "@/components/Tooltip";

// Real endpoint, confirmed against a live authenticated response given
// directly by the user: `GET /admin/users?role=ADMIN&screenType=management`
// — the exact same `/users` endpoint (`services/api/user.ts`, `userSlice`)
// Members.tsx already reads, filtered to admin-type accounts instead of
// members. See docs/decisions.md #78.
const ROWS_PER_PAGE = 10;

const getDisplayName = (user: User) => getDisplayNameBase(user, { fallbackLabel: "Admin" });

const formatLastActive = (value: string | null) => formatDate(value, { day: "numeric" });
const getRowActions = (admin: User,onEdit: (admin: User) => void, onRemove: (admin: User) => void): RowAction[] => {
  const name = getDisplayName(admin);
  return [
    {
      key: "edit",
      label: "Edit access",
      icon: <PencilIcon />,
      onClick: () => onEdit(admin),
    },
    {
      key: "remove",
      label: "Remove admin",
      icon: <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />,
      tone: "danger",
      dividerBefore: true,
      onClick: () => onRemove(admin),
    },
  ];
};

const buildAdminColumns = ( onEdit: (admin: User) => void, onRemove: (admin: User) => void): Column<User>[] => [
  {
    name: "Admin",
    width: "35%",
    cell: (row) => (
      <div className={styles.adminCell}>
        <span className={styles.avatar}>{getInitials(getDisplayName(row))}</span>
        <span className={styles.adminDetails}>
          <span className={styles.adminName}><TableCellTooltip value={getDisplayName(row)} /></span>
          <span className={styles.adminEmail}><TableCellTooltip value={row?.email || "—"} /></span>
        </span>
      </div>
    ),
  },
  // No "Role"/"Chapters" columns here — unlike the requested screenshot,
  // this endpoint's response has no per-admin role-name or chapter-scope
  // field (`userType`/`cityName` come back `null` for every admin in the
  // real sample seen). Dropped rather than faked, same as decision #68's
  // Audit Log filters. Role Permissions below is still the place role
  // scope is represented, even though it isn't per-admin here.
  { name: "Mobile", width: "20%", selector: (row) => row?.mobileNo || "—" },
  { name: "Last Active", width: "20%", selector: (row) => formatLastActive(row?.lastActiveAt) },
  {
    name: "",
    width: "15%",
    cell: (row) => (
      <RowActionsMenu actions={getRowActions(row,onEdit, onRemove)} ariaLabel={`Actions for ${getDisplayName(row)}`} />
    ),
  },
];

const getRoleRowActions = (
  role: Role,
  onEdit: (role: Role) => void,
  onDelete: (role: Role) => void
): RowAction[] => [
  {
    key: "edit",
    label: "Edit role",
    icon: <PencilIcon />,
    onClick: () => onEdit(role),
  },
  {
    key: "delete",
    label: "Delete role",
    icon: <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />,
    tone: "danger",
    dividerBefore: true,
    onClick: () => onDelete(role),
  },
];

const buildRoleColumns = (onEdit: (role: Role) => void, onDelete: (role: Role) => void): Column<Role>[] => [
  { name: "Role Name", width: "30%", selector: (row) => row?.name || "—" },
  { name: "Role Description", width: "50%", selector: (row) => row?.description || "—" },
  {
    name: "",
    width: "20%",
    cell: (row) => (
      <RowActionsMenu
        actions={getRoleRowActions(row, onEdit, onDelete)}
        ariaLabel={`Actions for ${row.name}`}
      />
    ),
  },
];

const TeamRoles = () => {
  const dispatch = useAppDispatch();
  const { users: admins, totalUsers, isFetching: isFetchingAdmins } = useAppSelector((state) => state.user);
  const { roles, totalRoles, isFetching: isFetchingRoles } = useAppSelector((state) => state.role);

  const [activeTab, setActiveTab] = useState("admins");
  const [adminsPage, setAdminsPage] = useState(1);
  const [rolesPage, setRolesPage] = useState(1);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isNewRoleModalOpen, setIsNewRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);
  const [deletingAdmin, setDeletingAdmin] = useState<User | null>(null);
  const lastFetchedRolesPage = useRef<number | null>(null);
  const [editingAdmin, setEditingAdmin] = useState<User | null>(null);

  useEffect(() => {
    dispatch(getAdminUsers({ pageNo: adminsPage, perPage: ROWS_PER_PAGE }));
  }, [dispatch, adminsPage]);

  useEffect(() => {
    if (lastFetchedRolesPage.current === rolesPage) return;
    dispatch(getRoles({ pageNo: rolesPage, perPage: ROWS_PER_PAGE }));
    lastFetchedRolesPage.current = rolesPage;
  }, [dispatch, rolesPage]);

  usePageSubtitle(`${totalUsers} admins · ${totalRoles} roles · SSO enforced`);

  const handleNewRoleModalClose = () => {
    setIsNewRoleModalOpen(false);
    setEditingRole(null);
  };

  const handleRoleFormSubmit = async (values: NewRoleFormValues & { permissionIds: number[] }) => {
    try {
      if (editingRole) {
        const res = await roleService.updateRole(editingRole.id, {
          name: values.roleName,
          description: values.description || null,
          permissionIds: values.permissionIds,
        });
        if (res?.success) {
          toast.success(res.message || "Role updated successfully.");
        }
      } else {
        const res = await roleService.createRole({ name: values.roleName, description: values.description || null });
        if (res?.success) {
          toast.success(res.message || "Role created successfully.");
        }
      }
      handleNewRoleModalClose();
      dispatch(getRoles({ pageNo: rolesPage, perPage: ROWS_PER_PAGE }));
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  const handleDeleteRoleConfirm = async () => {
    if (!deletingRole) return;
    try {
      const res = await roleService.deleteRole(deletingRole.id);
      if (res?.success) {
        toast.success(res.message || "Role deleted successfully.");
      }
      setDeletingRole(null);
      dispatch(getRoles({ pageNo: rolesPage, perPage: ROWS_PER_PAGE }));
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  const handleDeleteAdminConfirm = async () => {
    if (!deletingAdmin) return;
    try {
      const res = await userService.deleteUser(deletingAdmin.id);
      if (res?.success) {
        toast.success("Admin removed successfully.");
      }
      setDeletingAdmin(null);
      dispatch(getAdminUsers({ pageNo: adminsPage, perPage: ROWS_PER_PAGE }));
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  const adminColumns = useMemo(() => 
  buildAdminColumns((admin) => setEditingAdmin(admin),
  (admin) => setDeletingAdmin(admin)), []);

  const roleColumns = useMemo(
    () =>
      buildRoleColumns(
        (role) => {
          setEditingRole(role);
          setIsNewRoleModalOpen(true);
        },
        (role) => setDeletingRole(role),
      ),
    [],
  );

  const adminTabs = useMemo(
    () => [
      { label: "Admins", value: "admins", count: totalUsers },
      { label: "Roles", value: "roles", count: totalRoles },
    ],
    [totalUsers, totalRoles],
  );

  return (
    <>
      {/* Desktop View (Preserved exactly as existing) */}
      <div className={styles.desktopView}>
        <div className={styles.toolbar}>
          <CustomTabs tabs={adminTabs} activeTab={activeTab} onChange={setActiveTab} />
          {activeTab === "admins" ? (
            <Button
              startIcon={<PlusIcon width={14} height={14} />}
              className={styles.inviteButton}
              onClick={() => setIsInviteModalOpen(true)}
            >
              INVITE ADMIN
            </Button>
          ) : (
            <Button
              startIcon={<PlusIcon width={14} height={14} />}
              className={styles.inviteButton}
              onClick={() => {
                setEditingRole(null);
                setIsNewRoleModalOpen(true);
              }}
            >
              ADD ROLE
            </Button>
          )}
        </div>

        {activeTab === "admins" ? (
          <Table<User>
            columns={adminColumns}
            data={admins}
            progressPending={isFetchingAdmins}
            header={{ titleName: "Admin Users", showSearch: false, hideExport: true }}
            paginationProps={{
              isPagination: true,
              pageSelected: adminsPage,
              totalCount: totalUsers,
              rowsPerPageValue: ROWS_PER_PAGE,
              setPageSelected: setAdminsPage,
            }}
          />
        ) : (
          <Table<Role>
            columns={roleColumns}
            data={roles}
            progressPending={isFetchingRoles}
            header={{
              titleName: "Roles",
              showSearch: false,
              hideExport: true,
            }}
            paginationProps={{
              isPagination: true,
              pageSelected: rolesPage,
              totalCount: totalRoles,
              rowsPerPageValue: ROWS_PER_PAGE,
              setPageSelected: setRolesPage,
            }}
          />
        )}
      </div>

      {/* Mobile View (Classy Card-based layout for mobile screens only) */}
      <div className={styles.mobileView}>
        <div className={styles.mobileTabsWrapper}>
          <CustomTabs tabs={adminTabs} activeTab={activeTab} onChange={setActiveTab} />
        </div>

        <div className={styles.mobileSectionHeader}>
          <h2 className={styles.mobileSectionTitle}>
            {activeTab === "admins" ? `Admin Users (${totalUsers})` : `Roles (${totalRoles})`}
          </h2>
          {activeTab === "admins" ? (
            <Button
              startIcon={<PlusIcon width={13} height={13} />}
              className={styles.mobileInviteBtn}
              onClick={() => setIsInviteModalOpen(true)}
            >
              INVITE ADMIN
            </Button>
          ) : (
            <Button
              startIcon={<PlusIcon width={13} height={13} />}
              className={styles.mobileInviteBtn}
              onClick={() => {
                setEditingRole(null);
                setIsNewRoleModalOpen(true);
              }}
            >
              ADD ROLE
            </Button>
          )}
        </div>

        <div className={styles.mobileCardList}>
          {activeTab === "admins" ? (
            isFetchingAdmins ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className={styles.mobileSkeletonCard}>
                  <div className={styles.skeletonHeader}>
                    <div className={styles.skeletonAvatar} />
                    <div className={styles.skeletonTextGroup}>
                      <div className={styles.skeletonLineShort} />
                      <div className={styles.skeletonLineLong} />
                    </div>
                  </div>
                  <div className={styles.skeletonGrid}>
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                  </div>
                </div>
              ))
            ) : admins.length === 0 ? (
              <div className={styles.mobileEmptyState}>
                <h4 className={styles.mobileEmptyTitle}>No admin users found</h4>
              </div>
            ) : (
              admins.map((admin) => (
                <AdminCard
                  key={admin.id}
                  admin={admin}
                  onRemove={(adm) => setDeletingAdmin(adm)}
                />
              ))
            )
          ) : isFetchingRoles ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className={styles.mobileSkeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonAvatar} />
                  <div className={styles.skeletonTextGroup}>
                    <div className={styles.skeletonLineShort} />
                  </div>
                </div>
                <div className={styles.skeletonDivider} />
                <div className={styles.skeletonLineLong} />
              </div>
            ))
          ) : roles.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <h4 className={styles.mobileEmptyTitle}>No roles found</h4>
            </div>
          ) : (
            roles.map((role) => (
              <RoleCard
                key={role.id}
                role={role}
                onEdit={(r) => {
                  setEditingRole(r);
                  setIsNewRoleModalOpen(true);
                }}
                onDelete={(r) => setDeletingRole(r)}
              />
            ))
          )}
        </div>

        {activeTab === "admins" && totalUsers > ROWS_PER_PAGE && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              pageSelected={adminsPage}
              totalCount={totalUsers}
              rowsPerPageValue={ROWS_PER_PAGE}
              setPageSelected={setAdminsPage}
            />
          </div>
        )}

        {activeTab === "roles" && totalRoles > ROWS_PER_PAGE && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              pageSelected={rolesPage}
              totalCount={totalRoles}
              rowsPerPageValue={ROWS_PER_PAGE}
              setPageSelected={setRolesPage}
            />
          </div>
        )}
      </div>

      <InviteAdminModal
  isOpen={isInviteModalOpen || editingAdmin !== null}
  admin={editingAdmin}
  onClose={() => {
    setIsInviteModalOpen(false);
    setEditingAdmin(null);
  }}
  onUpdated={() => {
    dispatch(
      getAdminUsers({
        pageNo: adminsPage,
        perPage: ROWS_PER_PAGE,
      })
    );
  }}
/>
      <NewRoleModal
        isOpen={isNewRoleModalOpen}
        onClose={handleNewRoleModalClose}
        roleItem={editingRole}
        onSubmit={handleRoleFormSubmit}
      />

      <ConfirmDeleteModal
        isOpen={deletingRole !== null}
        onClose={() => setDeletingRole(null)}
        onConfirm={handleDeleteRoleConfirm}
        title="Delete Role"
        message={
          <>
            Are you sure you want to delete <strong>{deletingRole?.name}</strong>?
          </>
        }
      />

      <ConfirmDeleteModal
        isOpen={deletingAdmin !== null}
        onClose={() => setDeletingAdmin(null)}
        onConfirm={handleDeleteAdminConfirm}
        title="Remove Admin"
        message={
          deletingAdmin ? (
            <>
              Are you sure you want to remove <strong>{getDisplayName(deletingAdmin)}</strong>?
            </>
          ) : null
        }
      />
    </>
  );
};

export default TeamRoles;
